import type * as Marzipano from 'marzipano';
// Marzipano không export renderer — patch nội bộ để fade opacity từng tile khi textureLoad.
import WebGlBaseRenderer from 'marzipano/src/renderers/WebGlBase';

import { easeOutCubic } from './marzipanoTilePresentation';

const TILE_FADE_MS = 360;

const tileFadeStart = new WeakMap<object, number>();
const fadingTiles = new Set<object>();

let fadeRafId: number | null = null;
let patchInstalled = false;

function prefersReducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function getTileFadeOpacity(tile: object): number {
  if (prefersReducedMotion()) return 1;
  const start = tileFadeStart.get(tile);
  if (start == null) return 1;
  const t = Math.min(1, (performance.now() - start) / TILE_FADE_MS);
  return easeOutCubic(t);
}

function beginTileFade(tile: object, viewer: Marzipano.Viewer) {
  if (prefersReducedMotion()) return;
  tileFadeStart.set(tile, performance.now());
  fadingTiles.add(tile);
  scheduleFadeFrames(viewer);
}

function scheduleFadeFrames(viewer: Marzipano.Viewer) {
  if (fadeRafId != null) return;

  const step = () => {
    const now = performance.now();
    for (const tile of fadingTiles) {
      const start = tileFadeStart.get(tile);
      if (start == null) {
        fadingTiles.delete(tile);
        continue;
      }
      if (now - start >= TILE_FADE_MS) {
        tileFadeStart.delete(tile);
        fadingTiles.delete(tile);
      }
    }

    viewer.renderLoop().renderOnNextFrame();

    if (fadingTiles.size > 0) {
      fadeRafId = requestAnimationFrame(step);
    } else {
      fadeRafId = null;
    }
  };

  fadeRafId = requestAnimationFrame(step);
}

/** Gọi một lần khi tạo Viewer — fade-in tile mới, không ảnh hưởng tile đã nét. */
export function installMarzipanoTileFade(): void {
  if (patchInstalled) return;
  patchInstalled = true;

  const proto = WebGlBaseRenderer.prototype as {
    renderTile: (
      tile: object,
      texture: unknown,
      layer: { effects(): { opacity?: number } | null },
      layerZ: number
    ) => void;
    gl: WebGLRenderingContext;
    shaderProgram: { uOpacity: WebGLUniformLocation };
  };

  const original = proto.renderTile;
  proto.renderTile = function (tile, texture, layer, layerZ) {
    const effects = layer.effects();
    const layerOpacity = effects?.opacity ?? 1;
    const tileOpacity = texture ? getTileFadeOpacity(tile) : 1;
    this.gl.uniform1f(this.shaderProgram.uOpacity, layerOpacity * tileOpacity);
    original.call(this, tile, texture, layer, layerZ);
  };
}

export function bindSceneTileFade(
  scene: Marzipano.Scene,
  viewer: Marzipano.Viewer
): () => void {
  const layer = scene.layer();
  if (!layer) return () => {};

  const store = layer.textureStore();
  const onLoad = (tile: object) => beginTileFade(tile, viewer);

  store.addEventListener('textureLoad', onLoad);
  return () => store.removeEventListener('textureLoad', onLoad);
}
